import type { ButtonHTMLAttributes, ReactNode } from 'react';

// RMINT design-system Button primitive.
//
// Variants per the bundle's preview/buttons-primary.html and
// preview/buttons-secondary.html. Marketing red CTA (`--mk-red`)
// is intentionally NOT a variant — it ships as a separate component
// (MkButton) when apps/marketing/ exists. Keeps brand red boundary
// explicit and prevents accidental brand-red on a danger button.

export type ButtonVariant = 'primary' | 'inverse' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconOnly?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  children?: ReactNode;
};

const base =
  'inline-flex items-center justify-center font-semibold leading-none rounded-cta border transition-colors duration-150 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2';

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-primary-foreground border-primary hover:bg-primary-hover hover:border-primary-hover',
  inverse: 'bg-card text-primary border-primary hover:bg-primary-soft',
  ghost: 'bg-transparent text-foreground border-transparent hover:bg-muted',
  danger: 'bg-danger text-white border-danger hover:opacity-90',
};

const sizesText: Record<ButtonSize, string> = {
  sm: 'min-h-9 px-3 text-sm gap-1.5',
  md: 'min-h-11 px-6 text-base gap-2',
  lg: 'min-h-12 px-8 text-base gap-2',
};

const sizesIcon: Record<ButtonSize, string> = {
  sm: 'h-9 w-9',
  md: 'h-11 w-11',
  lg: 'h-12 w-12',
};

export function Button({
  variant = 'primary',
  size = 'md',
  iconOnly = false,
  leadingIcon,
  trailingIcon,
  className = '',
  children,
  ...rest
}: ButtonProps) {
  if (iconOnly && process.env.NODE_ENV !== 'production' && !rest['aria-label']) {
    // eslint-disable-next-line no-console
    console.warn('[@rmint/ui] <Button iconOnly> requires an aria-label for screen readers.');
  }

  const sizeClass = iconOnly ? sizesIcon[size] : sizesText[size];

  return (
    <button
      type={rest.type ?? 'button'}
      className={`${base} ${variants[variant]} ${sizeClass} ${className}`}
      {...rest}
    >
      {leadingIcon}
      {iconOnly ? null : children}
      {trailingIcon}
    </button>
  );
}
